import { LegacyOrganizationEntityListingPayload } from "./Admin";
import { MaintenanceFaultCodeResponse } from "./Maintenance";
import { TUser } from "./User";
import { TUserProfile } from "./UserProfile";

export type LegacyVehicleEntityListingPayload =
  LegacyOrganizationEntityListingPayload & {
    billingid?: string;
    deviceid?: string;
    driverid?: string;
    group_ids?: string;
    simid?: string;
    vehicleid?: string;
    isactive?: number;
    uniqueid?: string;
    trip_date_start?: number;
    trip_date_end?: number;

    // optional params for use in camera admin
    linked_to_camera?: boolean;
    linked_to_driver?: boolean;
  };

export type LegacyVehicleEntityFilterableListingPayload =
  LegacyOrganizationEntityListingPayload & {
    isactive?: number;
    exclude_ids?: string[];
  };

export type LegacyVehiclesEventsListingPayload = {
  account_id: string;
  timestamp?: number;
  device_id?: string;
  start_day_timestamp?: number;
  end_day_timestamp?: number;
};

export type SingleOrganizationVehiclePayload = {
  account_id: string | null;
  device_id: string;
};

export type EditOrganizationVehiclePayload = {
  account_id: string | null;
  device_id: string;
  data: any;
};

export type AllAssetLocationsPayload = {
  timestamp?: number;
  group_ids?: string;
};

export enum ASSET_MAP_STATUS {
  DRIVING = 1,
  IDLE = 2,
  NOT_REPORTING = 3,
  IGNITION_OFF = 4,
}

export type AssetLocationDetails = {
  accountid: string;
  asset_map_status: ASSET_MAP_STATUS;
  assetimageurl: string | null;
  border_color_code: string | null;
  description: string;
  deviceid: string;
  display_mode: string;
  displayname: string;
  drivername: string;
  equipmenttype: string;
  eventdata: {
    heading: number;
    latitude: number;
    longitude: number;
    statuscode: number;
    timestamp: number;
  };
  isactive: number;
  licenseplate: string;
  standard_icon_identifier: string | null;
  uniqueid: string;
  vehicleid: string;
  vehiclemake: string;
  vehiclemodel: string;
};

export type TLegacyVehicleGroup = {
  accountid: string;
  activeingroup: number;
  creationtime: number;
  deviceid: string;
  groupid: string;
  lastupdatetime: number;
};

export type TStatusCodeLabel = {
  statuscode: number;
  statusname: string | null;
  description: string | null;
};

export type TLegacyVehicle = {
  accountid: string;
  assetimageurl?: string;
  deviceid: string;
  groupid: string;
  group_ids?: string; // comma separated string for PATCH request - assign groups to vehicle
  equipmenttype: string; // (VIN) is manually entered in the form
  equipmentstatus: string;
  ignition_state: string;
  vehiclemake: string;
  vehiclemodel: string;
  vehicleid: string; // (VIN) is updated from OBD via the GPS device
  licenseplate: string;
  licenseexpire: number;
  insuranceexpire: number;
  driverid: string;
  drivername: string;
  driverstatus: number;
  fault_code_info: MaintenanceFaultCodeResponse;
  fuelcapacity: number;
  fueleconomy: number;
  fuelrateperhour: number;
  fuelcostperliter: number;
  fueltankprofile: string;
  speedlimitkph: number;
  plandistancekm: number;
  installtime: number;
  resettime: number;
  expirationtime: number;
  uniqueid: string;
  devicecode: string;
  devicetype: string;
  pushpinid: string;
  displaycolor: string;
  serialnumber: string;
  simphonenumber: string;
  simid: string;
  smsemail: string;
  imeinumber: string;
  datakey: string;
  ignitionindex: number;
  codeversion: string;
  featureset: string;
  ipaddressvalid: string;
  lasttotalconnecttime: number;
  lastduplexconnecttime: number;
  pendingpingcommand: string;
  lastpingtime: number;
  totalpingcount: number;
  maxpingcount: number;
  commandstatemask: number;
  expectack: number;
  expectackcode: number;
  lastackcommand: string;
  lastacktime: number;
  dcspropertiesid: string;
  dcsconfigmask: number;
  dcsconfigstring: string;
  dcscommandhost: string;
  supportsdmtp: number;
  supportedencodings: number;
  unitlimitinterval: number;
  maxallowedevents: number;
  totalprofilemask: string;
  totalmaxconn: number;
  totalmaxconnpermin: number;
  duplexprofilemask: string;
  duplexmaxconn: number;
  duplexmaxconnpermin: number;
  lasttcpsessionid: string;
  ipaddresscurrent: string;
  remoteportcurrent: number;
  listenportcurrent: number;
  lastinputstate: number;
  lastoutputstate: number;
  statuscodestate: number;
  lastbatterylevel: number;
  lastfuellevel: number;
  lastfueltotal: number;
  lastoillevel: number;
  lastvalidlatitude: number;
  lastvalidlongitude: number;
  lastvalidheading: number;
  lastvalidspeedkph: number;
  lastgpstimestamp: number;
  lasteventtimestamp: number;
  lastcellservinginfo: string;
  lastdistancekm: number;
  lastodometerkm: number;
  odometeroffsetkm: number;
  lastengineonhours: number;
  lastengineontime: number;
  lastengineofftime: number;
  lastenginehours: number;
  enginehoursoffset: number;
  lastignitiononhours: number;
  lastignitionontime: number;
  lastignitionofftime: number;
  lastignitionhours: number;
  laststoptime: number;
  laststarttime: number;
  lastmalfunctionlamp: number;
  lastfaultcode: string;
  isactive: 0 | 1; // TODO: [P] check if this is boolean
  displayname: string;
  description: string;
  notes: string;
  lastupdatetime: number;
  creationtime: number;
  allownotify: number;
  lastnotifytime: number;
  lastnotifycode: number;
  lastnotifyrule: string;
  notifyemail: string;
  notifyselector: string;
  notifyaction: number;
  notifydescription: string;
  notifysubject: string;
  notifytext: string;
  notifyusewrapper: number;
  notifypriority: number;
  parkedlatitude: number;
  parkedlongitude: number;
  parkedradius: number;
  assigneduserid: string;
  thermalprofile: string;
  hoursofoperation: string;
  pendingmessage: string;
  pendingmessageack: string;
  lasteventspersecond: number;
  lasteventspersecondms: number;
  bordercrossing: number;
  sparseeventreporting: number;
  lastbordercrosstime: number;
  maintintervalkm0: number;
  maintodometerkm0: number;
  maintintervalkm1: number;
  maintodometerkm1: number;
  maintintervalhr0: number;
  maintenghourshr0: number;
  maintnotes: string;
  remindermessage: string;
  reminderinterval: string;
  remindertime: number;
  lastservicetime: number;
  nextservicetime: number;
  lastdatapushtime: number;
  lasteventcreatemillis: number;
  parkedmaxspeedkph: number;
  vehicleyear: number | null;
  fueltankprofile2: string;
  lastfuellevel2: number;
  elogenabled: number;
  lastelogstate: string;
  fuelcapacity2: number;
  dcscommandstate: string;
  lastservice: number;
  serviceinterval: number;
  servicewindow: number;
  fixedtcpsessionid: string;
  maintintervalkm2: number;
  maintodometerkm2: number;
  maintintervalkm3: number;
  maintodometerkm3: number;
  maintintervalkm4: number;
  maintodometerkm4: number;
  maintintervalkm5: number;
  maintodometerkm5: number;
  maintintervalkm6: number;
  maintodometerkm6: number;
  maintintervalkm7: number;
  maintodometerkm7: number;
  maintintervalkm8: number;
  maintodometerkm8: number;
  maintintervalkm9: number;
  maintodometerkm9: number;
  maintintervalhr1: number;
  maintenghourshr1: number;
  maintintervalhr2: number;
  maintenghourshr2: number;
  maintintervalhr3: number;
  maintenghourshr3: number;
  maintintervalhr4: number;
  maintenghourshr4: number;
  lasteventmovedtime: number;
  maintfixedtimeft0: number;
  maintintervalft0: number;
  maintprenotifyft0: number;
  maintfixedtimeft1: number;
  maintintervalft1: number;
  maintprenotifyft1: number;
  maintfixedtimeft2: number;
  maintintervalft2: number;
  maintprenotifyft2: number;
  maintfixedtimeft3: number;
  maintintervalft3: number;
  maintprenotifyft3: number;
  maintfixedtimeft4: number;
  maintintervalft4: number;
  maintprenotifyft4: number;
  routesegcolorsel: string;
  cameraid: string;
  cameraonoff: number;
  assettype: string;
  drivertime: number;
  assetworksflag: number;
  fueltype: number;
  lastvbatteryvolts: number;
  simulateidlehours: number;
  insertidleendevent: number;
  lastidlehours: number;
  decodevin: number;
  lasthourspto1: number;
  maintptointervalhr1: number;
  maintptohourshr1: number;
  lasthourspto2: number;
  maintptointervalhr2: number;
  maintptohourshr2: number;
  lasthourspto3: number;
  maintptointervalhr3: number;
  maintptohourshr3: number;
  lasthourspto4: number;
  maintptointervalhr4: number;
  maintptohourshr4: number;
  recordertype: number;
  lastdatapulltime: number;
  vehicleclass: string;
  location: string;
  operator: string;
  dispatcherremarks: string;
  lastedittime: number;
  lastseatbeltmask: number;
  uniqueidlastchange: number;
  previousuniqueid: string;
  previousrecorderid: string;
  recorderidlastchange: number;
  lastvalidsatcount: number;
  lastvalidhdop: number;
  lastaddress: string;
  uidlastuniqueid: string;
  uidchangedtime: number;
  billingid: string;
  lasteventstatuscode: number;
  lastodometersrc: string;
  lastspeedsrc: string;
  lastenginehourssrc: string;
  standard_icon_identifier: string;
  display_mode: String;
  border_color_code: string;
  groups?: TLegacyVehicleGroup[];
  has_trips?: boolean;

  is_recording_supported?: boolean;

  custom_status_code_names?: TStatusCodeLabel[];
};

export type TLegacyVehicleEvent = {
  acceleration: number;
  accelerometerxyz: string;
  accountid: string;
  address: string;
  airfilterpressure: number;
  airpressure: number;
  altitude: number;
  analog0: number;
  analog1: number;
  analog2: number;
  analog3: number;
  attachdata: string;
  attachtype: string;
  batterylevel: number;
  batterytemp: number;
  batteryvolts: number;
  brakegforce: number;
  brakepos: number;
  brakepressure: number;
  cellaccuracy: number;
  celllatitude: number;
  celllongitude: number;
  cellservinginfo: string;
  celltimingadvance: number;
  celltowerid: number;
  city: string;
  coolantlevel: number;
  coolantpressure: number;
  coolanttemp: number;
  costcenter: number;
  country: string;
  creationmillis: number;
  creationtime: number;
  datapush: number;
  datasource: string;
  deviceid: string;
  distancekm: number;
  doorstatemask: number;
  driverid: string;
  drivermessage: string;
  driverstatus: number;
  enginehours: number;
  enginehourssrc: null;
  engineload: number;
  engineonhours: number;
  enginerpm: number;
  enginetemp: number;
  enginetorque: number;
  entityid: string;
  entitytype: number;
  etadistancekm: number;
  etalatitude: number;
  etalongitude: number;
  etatimestamp: number;
  etauniqueid: number;
  faultcode: string;
  frequencyhz: number;
  fueleconomy: number;
  fuelengineon: number;
  fuelidle: number;
  fuellevel: number;
  fuellevel2: number;
  fuelpressure: number;
  fuelpto: number;
  fuelremain: number;
  fueltemp: number;
  fueltotal: number;
  fueltrip: number;
  fuelusage: number;
  geozoneid: string;
  geozoneindex: number;
  gpsage: number;
  gpsfixstatus: number;
  gpsfixtype: number;
  hdop: number;
  heading: number;
  horzaccuracy: number;
  idlehours: number;
  ignitionstate: number;
  inputmask: number;
  intaketemp: number;
  istollroad: number;
  j1708fault: number;
  jobnumber: string;
  latitude: number;
  lightsstatemask: number;
  locationareacode: number;
  longitude: number;
  malfunctionlamp: number;
  massairflowrate: number;
  messageid: number;
  messagestatus: number;
  messagetimestamp: number;
  mobilecountrycode: number;
  mobilenetworkcode: number;
  odometerkm: number;
  odometeroffsetkm: number;
  odometersrc: null;
  oilcoolerintemp: number;
  oilcoolerouttemp: number;
  oillevel: number;
  oilpressure: number;
  oiltemp: number;
  outputmask: number;
  postalcode: string;
  priority: number;
  ptodistancekm: number;
  ptoengaged: number;
  ptohours: number;
  pulsecount: number;
  rawdata: string;
  recordid: number;
  rfidtag: string;
  satellitecount: number;
  seatbeltmask: number;
  sensorhigh: number;
  sensorlow: number;
  sequence: number;
  signalstrength: number;
  speedkph: number;
  speedlimitkph: number;
  speedsrc: null;
  stateprovince: string;
  statuscode: number;
  stopid: number;
  stopindex: number;
  stopstatus: number;
  streetaddress: string;
  subdivision: string;
  tanklevel: number;
  thermoaverage0: number;
  thermoaverage1: number;
  throttlepos: number;
  timestamp: number;
  tirepressure: string;
  tiretemp: string;
  transoiltemp: number;
  transportid: string;
  turbopressure: number;
  vbatteryvolts: number;
  vertaccuracy: number;
  workdistancekm: number;
  workhours: number;
};

export type TLegacyVehicleRecorderStatus = {
  access_token: string;
  cameras: { cameraId: number }[];
  recorder_status: "Unknown" | "standby" | "offline" | "online";
  imei: string;
  organization: number;
};

export type TLegacyVehicleRecorderMediaToken = {
  address: string;
  media_token: string;
};

export type OrganizationVehicle = {
  id: string;
  added_by: TUser & {
    profile: TUserProfile;
    is_active: boolean;
  };
  asset_type: string | null;
  country_code: string | null;
  created_at: string;
  email: string | null;
  equipment_status: string | null;
  euipment_type: string | null;
  firmware_version: string | null;
  fuel_capacity: number | null;
  fuel_cost: number | null;
  fuel_economy: number | null;
  fuel_type: string | null;
  group_pushin_id: number | null;
  ignition_input: string | null;
  imei_or_esn_number: number | null;
  is_active: boolean;
  licence_expiry: string | null;
  licence_plate: string;
  map_route_color: string | null;
  maximum_speed: number | null;
  organization: string;
  phone: number | null;
  prev_recorder_id: string | null;
  prev_unique_id: string | null;
  recorder_id: string | null;
  recorder_id_last_changed: string | null;
  recorder_on: string | null;
  recorder_type: string | null;
  serial_number: number | null;
  server_id: string | null;
  short_name: string | null;
  unique_id: string | null;
  unique_id_last_change: string | null;
  updated_at: string;
  vehicle_class: string | null;
  vehicle_description: string | null;
  vehicle_make: string;
  vehicle_model: number;
  vin: string;
  groups: any[];
  all_vehicles: boolean;
  driver: {
    id: number;
    name: string;
    phone: string;
  };
};
export type VehicleDecodeVinPayload = {
  asset_vin: string;
};
export type VehicleDecodeVinResponse = {
  Count: string;
  Message: string;
  SearchCriteria: string;
  Results: {
    Value: string;
    ValueId: string;
    Variable: string;
    VariableId: string;
  }[];
};

export type VehicleZipTieDetails = {
  id: number;
  deviceid: string;
  serial_number: string;
  created_at: string;
  created_by: string;
  created_by_name: string;
}
export type VehiclePatchZipTiePayload = {
  serial_number?: string;
  zip_tie_id: number;
};

export type VehicleAddZipTiePayload = {
  serial_number: string;
  device_id: string;
};